import { Alert, Box, CircularProgress, Typography } from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";

import { useMyReferralCode, useMyReferralStats } from "../../../../hooks/useReferral";
import { ReferralCodeInfo, ReferralStatsResponse } from "../../../../types/referral";

import ReferralCodeCard from "./ReferralCodeCard";
import ReferralHistoryTable from "./ReferralHistoryTable";
import ReferralStatsCards from "./ReferralStatsCards";

const ProfileReferral: React.FC = () => {
  const { t } = useTranslation();

  const {
    data: referralCodeResponse,
    isLoading: isCodeLoading,
  } = useMyReferralCode();

  const {
    data: referralStatsResponse,
    isLoading: isStatsLoading,
  } = useMyReferralStats();

  const isLoading = isCodeLoading || isStatsLoading;

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  const referralCode = referralCodeResponse?.data as ReferralCodeInfo | undefined;
  const referralStats = referralStatsResponse?.data as ReferralStatsResponse | undefined;

  if (!referralCode?.isEnabled) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="h5" fontWeight={600} gutterBottom>
          {t("profile.referral.title")}
        </Typography>
        <Alert severity="info" sx={{ mt: 2 }}>
          {t("profile.referral.programNotAvailable")}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1, sm: 2 } }}>
      <Typography variant="h5" fontWeight={600} gutterBottom sx={{ mb: 3 }}>
        {t("profile.referral.title")}
      </Typography>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        {/* Show welcome coupon if user was referred */}
        {referralCode.welcomeCouponCode && (
          <Alert
            severity={referralCode.welcomeCouponUsed ? "info" : "success"}
            sx={{ borderRadius: 2 }}
          >
            {referralCode.welcomeCouponUsed
              ? t("profile.referral.welcomeCouponUsed")
              : t("profile.referral.welcomeCouponAvailable", {
                  code: referralCode.welcomeCouponCode,
                })}
          </Alert>
        )}

        <ReferralCodeCard
          referralCode={referralCode.referralCode}
          referralLink={referralCode.referralLink}
          discountValue={referralCode.discountValue}
          discountType={referralCode.discountType}
          promotionalText={referralCode.promotionalText}
        />

        <ReferralStatsCards
          totalReferrals={referralStats?.totalReferrals ?? 0}
          completedReferrals={referralStats?.completedReferrals ?? 0}
          pendingReferrals={referralStats?.pendingReferrals ?? 0}
          remainingReferrals={referralCode.remainingReferrals}
        />

        <ReferralHistoryTable referrals={referralStats?.referrals ?? []} />
      </Box>
    </Box>
  );
};

export default ProfileReferral;
